import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Eye, Loader2, Plus, Trash2, XCircle } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import dayjs from 'dayjs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { useBreadcrumbs } from 'contexts/BreadcrumbsContext';
import {
  createVendorContract, FormOptions, getFormOptions, getVendorContract, INDIAN_STATES, Lane, PanVerifyResult,
  previewUnsaved, resubmitVendorContract, VendorContractDetail, VendorContractInput, verifyPan,
} from './api';
import PdfPreviewDialog from './PdfPreviewDialog';

const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const EMPTY_LANE: Lane = { route: '', vehicle_type: '', rate: '', rate_basis: '', transit_days: '' };

const EMPTY: VendorContractInput = {
  vendor_name: '', vendor_pan: '', vendor_type: 'broker', load_type: 'FTL',
  address_line1: '', address_line2: '', city: '', state: '', pincode: '',
  signatory_name: '', contact_phone: '', contact_email: '',
  lanes: [{ ...EMPTY_LANE }],
  security_deposit: '', placement_hours: '', free_time_hours: '',
  detention_per_day: '', late_delivery_per_day: '', pod_delay_per_day: '',
  diesel_threshold_pct: '', diesel_faf_pct: '', diesel_base_price: '', diesel_base_city: '', diesel_base_date: '',
  non_placement_penalty: '', advance_pct: '', balance_days: '',
  valid_from: '', valid_to: '',
};

const s = (v: unknown) => (v === null || v === undefined ? '' : String(v));

function fromDetail(d: VendorContractDetail): VendorContractInput {
  const out = { ...EMPTY } as Record<string, unknown>;
  (Object.keys(EMPTY) as (keyof VendorContractInput)[]).forEach((k) => {
    if (k !== 'lanes') out[k] = s((d as any)[k]);
  });
  out.lanes = d.lanes.map((l) => ({
    route: l.route, vehicle_type: l.vehicle_type, rate: s(l.rate), rate_basis: l.rate_basis, transit_days: s(l.transit_days),
  }));
  return out as VendorContractInput;
}

type Errors = Record<string, string>;

function validate(f: VendorContractInput): Errors {
  const e: Errors = {};
  const req = (k: keyof VendorContractInput, label: string) => { if (!s(f[k]).trim()) e[k] = `${label} is required`; };
  req('vendor_name', 'Vendor name');
  if (!PAN_RE.test(f.vendor_pan)) e.vendor_pan = 'Enter a valid PAN (e.g. ABCDE1234F)';
  req('address_line1', 'Address');
  req('city', 'City');
  req('state', 'State');
  if (!/^[1-9][0-9]{5}$/.test(f.pincode)) e.pincode = 'Enter a 6-digit PIN code';
  req('signatory_name', 'Signatory name');
  if (f.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.contact_email)) e.contact_email = 'Enter a valid email';
  f.lanes.forEach((l, i) => {
    if (!l.route.trim()) e[`lane${i}.route`] = 'Required';
    if (!l.vehicle_type.trim()) e[`lane${i}.vehicle_type`] = 'Required';
    if (!(Number(l.rate) > 0)) e[`lane${i}.rate`] = 'Required';
    if (!l.rate_basis.trim()) e[`lane${i}.rate_basis`] = 'Required';
    if (!(Number(l.transit_days) > 0) || !Number.isInteger(Number(l.transit_days))) e[`lane${i}.transit_days`] = 'Whole days';
  });
  const num = (k: keyof VendorContractInput, label: string, { min = 0, max = Infinity, int = false, gt = false } = {}) => {
    const raw = s(f[k]).trim();
    const v = Number(raw);
    if (!raw || Number.isNaN(v)) e[k] = `${label} is required`;
    else if (gt ? v <= min : v < min) e[k] = `Must be ${gt ? 'more than' : 'at least'} ${min}`;
    else if (v > max) e[k] = `Must be at most ${max}`;
    else if (int && !Number.isInteger(v)) e[k] = 'Whole number';
  };
  num('security_deposit', 'Security deposit');
  num('placement_hours', 'Placement timeline', { min: 0, gt: true, int: true });
  num('free_time_hours', 'Free time', { int: true });
  num('detention_per_day', 'Detention charges');
  num('late_delivery_per_day', 'Late delivery penalty');
  num('pod_delay_per_day', 'POD delay penalty');
  num('diesel_threshold_pct', 'Threshold', { gt: true, max: 100 });
  num('diesel_faf_pct', 'FAF', { gt: true, max: 100 });
  num('diesel_base_price', 'Base diesel price', { gt: true });
  req('diesel_base_city', 'City');
  req('diesel_base_date', 'Date');
  req('non_placement_penalty', 'Non-placement penalty');
  num('advance_pct', 'Advance', { max: 100 });
  num('balance_days', 'Balance days', { int: true, max: 365 });
  req('valid_from', 'Start date');
  req('valid_to', 'End date');
  if (f.valid_from && f.valid_to && f.valid_to <= f.valid_from) e.valid_to = 'Must be after the start date';
  return e;
}

// ---- layout bits ----
const Section: React.FC<{ title: string; children: React.ReactNode; action?: React.ReactNode }> = ({ title, children, action }) => (
  <section className="rounded-lg border bg-card">
    <div className="flex items-center justify-between gap-3 border-b px-4 py-2">
      <h2 className="text-sm font-semibold">{title}</h2>
      {action}
    </div>
    <div className="px-4 py-3">{children}</div>
  </section>
);

const Field: React.FC<{ label: string; error?: string; required?: boolean; className?: string; children: React.ReactNode }> = ({ label, error, required = true, className, children }) => (
  <div className={className}>
    <Label className="mb-1 block text-xs">{label}{required && <span className="text-red-500"> *</span>}</Label>
    {children}
    {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
  </div>
);

const Affix: React.FC<{ before?: string; after?: string; children: React.ReactNode }> = ({ before, after, children }) => (
  <div className="relative">
    {before && <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{before}</span>}
    {children}
    {after && <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{after}</span>}
  </div>
);

// ---- page ----
const VendorContractFormPage: React.FC = () => {
  const { id } = useParams();
  const editingId = id ? Number(id) : null;
  const navigate = useNavigate();
  const { setBreadcrumbs } = useBreadcrumbs();

  const [form, setForm] = useState<VendorContractInput>(EMPTY);
  const [editing, setEditing] = useState<VendorContractDetail | null>(null);
  const [options, setOptions] = useState<FormOptions | null>(null);
  const [errors, setErrors] = useState<Errors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState<Blob | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    setBreadcrumbs([
      { label: 'Contracts', href: '/contracts' },
      { label: 'Vendor Contracts', href: '/contracts/vendor' },
      { label: editingId ? (editing?.contract_number || 'Edit') : 'New contract' },
    ]);
  }, [setBreadcrumbs, editingId, editing?.contract_number]);

  useEffect(() => { getFormOptions().then(setOptions).catch(() => {}); }, []);
  useEffect(() => {
    if (!editingId) return;
    getVendorContract(editingId)
      .then((d) => {
        if (!d.can_edit) { setLoadError('Only a rejected contract can be edited, by the person who submitted it.'); return; }
        setEditing(d);
        setForm(fromDetail(d));
      })
      .catch((e: any) => setLoadError(e?.message || 'Could not load the contract'));
  }, [editingId]);

  const clearError = (key: string) => setErrors((e) => {
    if (!(key in e)) return e;
    const next = { ...e };
    delete next[key];
    return next;
  });
  const set = <K extends keyof VendorContractInput>(k: K, v: VendorContractInput[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    clearError(k);
  };
  const setLane = (i: number, k: keyof Lane, v: string) => {
    setForm((f) => ({ ...f, lanes: f.lanes.map((l, j) => (j === i ? { ...l, [k]: v } : l)) }));
    clearError(`lane${i}.${k}`);
  };
  const err = (k: string) => errors[k];

  // ---- template availability: pairs without a template show disabled ----
  const hasTemplate = (load: string, vendor: string) => !!options?.templates.some((t) => t.load_type === load && t.vendor_type === vendor);

  // ---- PAN check: when the PAN (or the name, once a PAN is in) is filled in ----
  const [pan, setPan] = useState<{ state: 'idle' | 'checking' | 'done' | 'error'; result?: PanVerifyResult; error?: string }>({ state: 'idle' });
  const lastChecked = useRef('');
  const checkPan = async () => {
    const p = form.vendor_pan.trim().toUpperCase();
    const key = `${p}|${form.vendor_name.trim()}`;
    if (!PAN_RE.test(p) || key === lastChecked.current) return;
    lastChecked.current = key;
    setPan({ state: 'checking' });
    try {
      setPan({ state: 'done', result: await verifyPan(p, form.vendor_name.trim()) });
    } catch (e: any) {
      lastChecked.current = '';
      setPan({ state: 'error', error: e?.message || 'PAN check failed' });
    }
  };
  useEffect(() => {
    if (editing && PAN_RE.test(editing.vendor_pan)) checkPan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const panInvalid = pan.state === 'done' && pan.result && !pan.result.valid;

  const doPreview = async () => {
    const e = validate(form);
    setErrors(e);
    if (Object.keys(e).length) { setSubmitError('Fix the highlighted fields to preview the agreement.'); return; }
    setSubmitError(null);
    setPreviewing(true);
    try {
      setPreview(await previewUnsaved(form));
    } catch (ex: any) {
      setSubmitError(ex?.message || 'Could not render the preview');
    } finally {
      setPreviewing(false);
    }
  };

  const submit = async () => {
    const e = validate(form);
    setErrors(e);
    if (Object.keys(e).length) {
      setSubmitError(`${Object.keys(e).length} field${Object.keys(e).length === 1 ? ' needs' : 's need'} attention.`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (panInvalid) { setSubmitError('The PAN is not valid — check it before submitting.'); return; }
    setSubmitError(null);
    setSaving(true);
    try {
      if (editingId) {
        await resubmitVendorContract(editingId, form);
        navigate(`/contracts/vendor/${editingId}`);
      } else {
        const res = await createVendorContract(form);
        navigate(`/contracts/vendor/${res.id}`);
      }
    } catch (ex: any) {
      setSubmitError(ex?.message || 'Could not submit the contract');
    } finally {
      setSaving(false);
    }
  };

  if (loadError) {
    return (
      <div className="px-4 sm:px-6 pb-6 space-y-3">
        <h1 className="text-2xl font-semibold">Can't edit this contract</h1>
        <p className="text-sm text-muted-foreground">{loadError}</p>
        <Button variant="outline" onClick={() => navigate(editingId ? `/contracts/vendor/${editingId}` : '/contracts/vendor')}>Back</Button>
      </div>
    );
  }
  if (editingId && !editing) return <div className="px-4 sm:px-6 pb-6 text-muted-foreground">Loading…</div>;

  return (
    <div className="px-4 sm:px-6 pb-10">
      <div className="mx-auto max-w-6xl space-y-3 [&_input]:h-9 [&_button[role=combobox]]:h-9">
        <div>
          <h1 className="text-xl font-semibold">{editingId ? `Edit ${editing?.contract_number}` : 'New vendor contract'}</h1>
        </div>

        {editing?.rejection_reason && (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            <p className="font-medium">Rejected by {editing.user_names[editing.rejected_by || ''] || editing.rejected_by}</p>
            <p className="mt-0.5">{editing.rejection_reason}</p>
            <p className="mt-1 text-xs text-red-700">Make the changes below and resubmit — it goes back for approval with the same agreement number.</p>
          </div>
        )}

        {submitError && <div className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{submitError}</div>}

        {/* ---- vendor ---- */}
        <Section title="Vendor">
          <div className="grid grid-cols-1 gap-x-3 gap-y-2.5 md:grid-cols-12">
            <Field label="Vendor name" error={err('vendor_name')} className="md:col-span-4">
              <Input value={form.vendor_name} onChange={(e) => set('vendor_name', e.target.value)} onBlur={checkPan} />
            </Field>
            <Field label="PAN" error={err('vendor_pan')} className="md:col-span-2">
              <Input
                value={form.vendor_pan}
                maxLength={10}
                className="font-mono uppercase"
                onChange={(e) => { set('vendor_pan', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')); setPan({ state: 'idle' }); lastChecked.current = ''; }}
                onBlur={checkPan}
               
              />
            </Field>


            <Field label="Vendor type" className="md:col-span-3">
              <Select value={form.vendor_type} onValueChange={(v) => set('vendor_type', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(options?.vendor_types ?? [{ value: 'broker', label: 'Broker' }]).map((o) => {
                    const ok = hasTemplate(form.load_type, o.value);
                    return <SelectItem key={o.value} value={o.value} disabled={!ok}>{o.label}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Load type" className="md:col-span-3">
              <Select value={form.load_type} onValueChange={(v) => set('load_type', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(options?.load_types ?? [{ value: 'FTL', label: 'Full Truck Load (FTL)' }]).map((o) => {
                    const ok = hasTemplate(o.value, form.vendor_type);
                    return <SelectItem key={o.value} value={o.value} disabled={!ok}>{o.label}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </Field>

            {pan.state !== 'idle' && <div className="md:col-span-12"><PanStatus pan={pan} onRetry={checkPan} /></div>}
            <Field label="Address line 1" error={err('address_line1')} className="md:col-span-3">
              <Input value={form.address_line1} onChange={(e) => set('address_line1', e.target.value)} />
            </Field>
            <Field label="Address line 2" required={false} className="md:col-span-3">
              <Input value={form.address_line2} onChange={(e) => set('address_line2', e.target.value)} />
            </Field>
            <Field label="City" error={err('city')} className="md:col-span-2">
              <Input value={form.city} onChange={(e) => set('city', e.target.value)} />
            </Field>
            <Field label="State" error={err('state')} className="md:col-span-2">
              <Select value={form.state} onValueChange={(v) => set('state', v)}>
                <SelectTrigger><SelectValue placeholder="Select state" /></SelectTrigger>
                <SelectContent>{INDIAN_STATES.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="PIN code" error={err('pincode')} className="md:col-span-2">
              <Input value={form.pincode} inputMode="numeric" maxLength={6} onChange={(e) => set('pincode', e.target.value.replace(/\D/g, ''))} />
            </Field>

            <Field label="Vendor's signatory" error={err('signatory_name')} className="md:col-span-4">
              <Input value={form.signatory_name} onChange={(e) => set('signatory_name', e.target.value)} />
            </Field>
            <Field label="Phone" required={false} className="md:col-span-4">
              <Input value={form.contact_phone} inputMode="tel" onChange={(e) => set('contact_phone', e.target.value)} />
            </Field>
            <Field label="Email" required={false} error={err('contact_email')} className="md:col-span-4">
              <Input type="email" value={form.contact_email} onChange={(e) => set('contact_email', e.target.value)} />
            </Field>
          </div>
        </Section>

        {/* ---- lanes ---- */}
        <Section
          title="Routes & freight"
         
          action={<Button variant="outline" size="sm" onClick={() => setForm((f) => ({ ...f, lanes: [...f.lanes, { ...EMPTY_LANE }] }))}>
            <Plus className="mr-1 h-4 w-4" /> Add lane
          </Button>}
        >
          <div className="space-y-2">
            {form.lanes.map((l, i) => (
              <div key={i} className="rounded-md border bg-muted/20 px-3 py-2">
                <div className="mb-1 flex h-6 items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Lane {i + 1}</span>
                  {form.lanes.length > 1 && (
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-muted-foreground hover:text-red-600"
                      onClick={() => setForm((f) => ({ ...f, lanes: f.lanes.filter((_, j) => j !== i) }))}>
                      <Trash2 className="mr-1 h-4 w-4" /> Remove
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 gap-x-3 gap-y-2.5 md:grid-cols-12">
                  <Field label="Route / lane" error={err(`lane${i}.route`)} className="md:col-span-3">
                    <Input value={l.route} onChange={(e) => setLane(i, 'route', e.target.value)} />
                  </Field>
                  <Field label="Vehicle type" error={err(`lane${i}.vehicle_type`)} className="md:col-span-2">
                    <Input value={l.vehicle_type} onChange={(e) => setLane(i, 'vehicle_type', e.target.value)} />
                  </Field>
                  <Field label="Lorry hire rate" error={err(`lane${i}.rate`)} className="md:col-span-2">
                    <Affix before="₹"><Input type="number" min={0} className="pl-7" value={s(l.rate)} onChange={(e) => setLane(i, 'rate', e.target.value)} /></Affix>
                  </Field>
                  <Field label="Rate basis" error={err(`lane${i}.rate_basis`)} className="md:col-span-3">
                    <Input value={l.rate_basis} onChange={(e) => setLane(i, 'rate_basis', e.target.value)} />
                  </Field>
                  <Field label="Transit time" error={err(`lane${i}.transit_days`)} className="md:col-span-2">
                    <Affix after="days"><Input type="number" min={1} className="pr-12" value={s(l.transit_days)} onChange={(e) => setLane(i, 'transit_days', e.target.value)} /></Affix>
                  </Field>
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* ---- commercial terms ---- */}
        <Section title="Commercial terms">
          <div className="grid grid-cols-1 gap-x-3 gap-y-2.5 md:grid-cols-4">
            <Field label="Security deposit / PG" error={err('security_deposit')}>
              <Affix before="₹"><Input type="number" min={0} className="pl-7" value={form.security_deposit} onChange={(e) => set('security_deposit', e.target.value)} /></Affix>
            </Field>
            <Field label="Vehicle placement timeline" error={err('placement_hours')}>
              <Affix after="hours"><Input type="number" min={1} className="pr-14" value={form.placement_hours} onChange={(e) => set('placement_hours', e.target.value)} /></Affix>
            </Field>
            <Field label="Loading / unloading free time" error={err('free_time_hours')}>
              <Affix after="hours"><Input type="number" min={0} className="pr-14" value={form.free_time_hours} onChange={(e) => set('free_time_hours', e.target.value)} /></Affix>
            </Field>
            <Field label="Detention charges" error={err('detention_per_day')}>
              <Affix before="₹" after="/ day"><Input type="number" min={0} className="pl-7 pr-14" value={form.detention_per_day} onChange={(e) => set('detention_per_day', e.target.value)} /></Affix>
            </Field>
            <Field label="Late delivery penalty" error={err('late_delivery_per_day')}>
              <Affix before="₹" after="/ day"><Input type="number" min={0} className="pl-7 pr-14" value={form.late_delivery_per_day} onChange={(e) => set('late_delivery_per_day', e.target.value)} /></Affix>
            </Field>
            <Field label="POD delay penalty" error={err('pod_delay_per_day')}>
              <Affix before="₹" after="/ day"><Input type="number" min={0} className="pl-7 pr-14" value={form.pod_delay_per_day} onChange={(e) => set('pod_delay_per_day', e.target.value)} /></Affix>
            </Field>
            <Field label="Non-placement penalty" error={err('non_placement_penalty')} className="md:col-span-2">
              <Input value={form.non_placement_penalty} onChange={(e) => set('non_placement_penalty', e.target.value)} />
            </Field>
          </div>

          <h3 className="mb-2 mt-4 text-sm font-semibold">Diesel escalation / de-escalation</h3>
          <div className="grid grid-cols-1 gap-x-3 gap-y-2.5 md:grid-cols-5">
            <Field label="Threshold (±)" error={err('diesel_threshold_pct')}>
              <Affix after="%"><Input type="number" min={0} className="pr-8" value={form.diesel_threshold_pct} onChange={(e) => set('diesel_threshold_pct', e.target.value)} /></Affix>
            </Field>
            <Field label="FAF" error={err('diesel_faf_pct')}>
              <Affix after="%"><Input type="number" min={0} className="pr-8" value={form.diesel_faf_pct} onChange={(e) => set('diesel_faf_pct', e.target.value)} /></Affix>
            </Field>
            <Field label="Base diesel price" error={err('diesel_base_price')}>
              <Affix before="₹" after="/ L"><Input type="number" min={0} step="0.01" className="pl-7 pr-10" value={form.diesel_base_price} onChange={(e) => set('diesel_base_price', e.target.value)} /></Affix>
            </Field>
            <Field label="Base city" error={err('diesel_base_city')}>
              <Input value={form.diesel_base_city} onChange={(e) => set('diesel_base_city', e.target.value)} />
            </Field>
            <Field label="Price as on" error={err('diesel_base_date')}>
              <Input type="date" value={form.diesel_base_date} onChange={(e) => set('diesel_base_date', e.target.value)} />
            </Field>
          </div>
          <DieselSentence f={form} />

          <h3 className="mb-2 mt-4 text-sm font-semibold">Payment & validity</h3>
          <div className="grid grid-cols-1 gap-x-3 gap-y-2.5 md:grid-cols-4">
            <Field label="Advance" error={err('advance_pct')}>
              <Affix after="%"><Input type="number" min={0} max={100} className="pr-8" value={form.advance_pct} onChange={(e) => set('advance_pct', e.target.value)} /></Affix>
            </Field>
            <Field label="Balance paid within" error={err('balance_days')}>
              <Affix after="days"><Input type="number" min={0} className="pr-12" value={form.balance_days} onChange={(e) => set('balance_days', e.target.value)} /></Affix>
            </Field>
            <Field label="Valid from" error={err('valid_from')}>
              <Input type="date" value={form.valid_from} onChange={(e) => set('valid_from', e.target.value)} />
            </Field>
            <Field label="Valid to" error={err('valid_to')}>
              <Input type="date" value={form.valid_to} min={form.valid_from || undefined} onChange={(e) => set('valid_to', e.target.value)} />
            </Field>
          </div>
          <PaymentSentence f={form} />
        </Section>

        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button variant="outline" onClick={() => navigate(editingId ? `/contracts/vendor/${editingId}` : '/contracts/vendor')}>Cancel</Button>
          <Button variant="outline" onClick={doPreview} disabled={previewing}>
            {previewing ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Eye className="mr-1 h-4 w-4" />} Preview agreement
          </Button>
          <Button onClick={submit} disabled={saving || pan.state === 'checking'}>
            {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            {editingId ? 'Resubmit for approval' : 'Submit for approval'}
          </Button>
        </div>
      </div>

      <PdfPreviewDialog title="Agreement preview (draft)" blob={preview} onClose={() => setPreview(null)} />
    </div>
  );
};

// ---- PAN verification result under the PAN field ----
const PanStatus: React.FC<{ pan: { state: string; result?: PanVerifyResult; error?: string }; onRetry: () => void }> = ({ pan, onRetry }) => {
  if (pan.state === 'idle') return null;
  if (pan.state === 'checking') return <p className="flex items-center gap-1.5 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Verifying PAN…</p>;
  if (pan.state === 'error') {
    return (
      <p className="flex items-center gap-1.5 text-sm text-amber-700">
        <AlertTriangle className="h-4 w-4" /> {pan.error}
        <button className="ml-1 font-medium underline" onClick={onRetry}>Retry</button>
      </p>
    );
  }
  const r = pan.result!;
  if (!r.valid) {
    return <p className="flex items-center gap-1.5 text-sm text-red-700"><XCircle className="h-4 w-4" /> PAN is not valid ({r.pan_status || 'not found'}).</p>;
  }
  return (
    <div className="space-y-1">
      <p className="flex items-center gap-1.5 text-sm text-green-700">
        <CheckCircle2 className="h-4 w-4" /> Valid PAN — registered to <span className="font-medium">{r.registered_name || '—'}</span>
      </p>
      {r.name_match === false && (
        <p className="flex items-center gap-1.5 rounded bg-amber-50 px-2 py-1 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 shrink-0" /> The vendor name doesn't match the PAN's registered name. You can still submit — the approver will see this warning.
        </p>
      )}
    </div>
  );
};

// ---- live wording, so the person filling the form sees what will print ----
const Sentence: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="mt-2 rounded-md border-l-2 border-blue-300 bg-blue-50/50 px-3 py-2 text-xs italic text-muted-foreground">{children}</p>
);

const DieselSentence: React.FC<{ f: VendorContractInput }> = ({ f }) => (
  <Sentence>
    Once diesel price changes beyond ±{f.diesel_threshold_pct || '…'}% from the base diesel price, FAF shall be applicable at{' '}
    {f.diesel_faf_pct || '…'}% of the increase or decrease in diesel cost. The base diesel price shall be the {f.diesel_base_city || '…'} diesel
    rate of Rs. {f.diesel_base_price || '…'} per litre as on {f.diesel_base_date ? dayjs(f.diesel_base_date).format('D MMMM YYYY') : '…'}.
  </Sentence>
);

const PaymentSentence: React.FC<{ f: VendorContractInput }> = ({ f }) => {
  const adv = Number(f.advance_pct);
  const days = f.balance_days || '…';
  const tail = `${days} day${days === '1' ? '' : 's'} after delivery of the consignment and submission of a clean Proof of Delivery (POD)`;
  const text = f.advance_pct === '' ? `…% advance and balance in ${tail}`
    : adv === 0 ? `100% payment in ${tail}`
    : adv === 100 ? '100% advance'
    : `${f.advance_pct}% advance and balance in ${tail}`;
  return <Sentence>Payment terms: {text}.</Sentence>;
};

export default VendorContractFormPage;
